import { LoginMethodEnum } from '@/constant/enum';
import { useAccountStore } from '@/stores/user';
import cookie from 'js-cookie';
const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN;
import { CSDN_BLOG_SOURCE } from '@/constant';

const registerLoginMethodMap = {
  [LoginMethodEnum.PHONE]: '短信',
  [LoginMethodEnum.PASSWORD]: '密码',
  [LoginMethodEnum.MINI_PROGRAM]: '小程序登录',
  [LoginMethodEnum.CSDN]: 'CSDN',
  [LoginMethodEnum.GITEE]: 'Gitee',
  [LoginMethodEnum.GITHUB]: 'GitHub',
  [LoginMethodEnum.HUAWEI]: '华为',
  [LoginMethodEnum.WECHAT_OPEN]: '微信',
  [LoginMethodEnum.WECHAT_MP]: '微信',
  [LoginMethodEnum.H_BUILDER]: 'Hbuilder',
  [LoginMethodEnum.H_BUILDER_PLUGIN]: 'Hbuilder插件',
  [LoginMethodEnum.ATOMGIT]: 'AtomGit'
};
const utmSourceMap = {
  'csdn-blog-iframe': '快应用',
  'miniapp': '小程序',
  'web': '网页'
};

/**
 * 火山引擎埋点
 * 上报注册登录事件
 * @param isRegister 是否注册
 * @param registerLoginMethod 注册登录方式
 * @param success 是否成功
 * @param extraData 额外数据, 比如 oauth 的回跳地址 target_url
 */
export const collectRegisterLoginEvent = ({ isRegister, registerLoginMethod, success, extraData = {}, username }: { isRegister: boolean, registerLoginMethod: string, success: boolean; extraData?: Record<string, any>; username?: string; }) => {
  if (!window.collectEvent) {
    return;
  }

  const searchParams = Object.fromEntries(new URLSearchParams(location.search).entries());

  const reportData: Record<string, any> = {
    register_login_type: isRegister ? '注册' : '登录',
    register_login_result: success ? '成功' : '失败',
    register_login_source: utmSourceMap[searchParams['utm_source'] as keyof typeof utmSourceMap] || '',
    register_login_method: registerLoginMethodMap[registerLoginMethod as keyof typeof registerLoginMethodMap] || '',
    article_id: searchParams['from_id'] || '',
    userid_fast_application: searchParams['uuid_tt_dd'] || '',
    gitcode_utm_source: searchParams['utm_source'] || '',
    page_url: location.href || '',
    env: import.meta.env.VITE_ENV,
    namespace: extraData.stateFrom || '',
    ...searchParams,
    ...extraData
  };
  delete reportData['from_id'];
  delete reportData['uuid_tt_dd'];
  delete reportData['utm_source'];
  if (extraData?.stateFrom?.includes(CSDN_BLOG_SOURCE)) {
    window.opener?.postMessage({
      type: 'main_report',
      report: { isRegister, registerLoginMethod, success, extraData, username: username || useAccountStore().accountInfo?.username || '' }
    }, '*');
  } else {
    if (extraData.stateFrom) {
      cookie.set('stateFrom', extraData.stateFrom, { domain: cookieDomain });
    }
    // 是否注册，是否成功，方式增加到cookie中, 方便在其他页面获取
    cookie.set('isRegister', String(isRegister), { domain: cookieDomain });
    cookie.set('success', String(success), { domain: cookieDomain });
    cookie.set('registerLoginMethod', String(registerLoginMethod), { domain: cookieDomain });
  }
};

/**
 * 页面 PV/UV 上报埋点
 * 用于在页面加载时收集并上报页面访问（PV/UV）相关数据，便于统计分析用户访问行为。
 *
 * @param {Object} params - 上报参数对象
 * @param {string} params.pageTitle - 当前页面标题
 *
 * 主要上报字段说明：
 * - page_title: 页面标题
 * - page_url: 当前页面完整 URL
 * - env: 当前运行环境（如 development、production 等）
 * - username: 用户名
 * - article_id: 来源文章 ID（如有 from_id 参数）
 * - userid_fast_application: 快应用用户唯一标识（如有 uuid_tt_dd 参数）
 * - gitcode_utm_source: 推广来源（如有 utm_source 参数）
 * - user_unique_id: 用户唯一标识（有 username 时额外补充）
 * - 其余 URL 查询参数也会一并上报
 *
 * 注意：会自动去除 from_id、uuid_tt_dd、utm_source 字段，避免重复。
 */
export const collectPageLoadingEvent = ({ pageTitle = '' }:{pageTitle:string}) => {
  if (!window.collectEvent) {
    return;
  }

  const searchParams = Object.fromEntries(new URLSearchParams(location.search).entries());

  const username = useAccountStore().accountInfo?.username || '';

  const reportData: Record<string, any> = {
    page_title: pageTitle,
    page_url: location.href || '',
    env: import.meta.env.VITE_ENV,
    username,
    article_id: searchParams['from_id'] || '',
    userid_fast_application: searchParams['uuid_tt_dd'] || '',
    gitcode_utm_source: searchParams['utm_source'] || '',
    ...searchParams
  };

  if (username) {
    reportData['user_unique_id'] = username;
  }

  delete reportData['from_id'];
  delete reportData['uuid_tt_dd'];
  delete reportData['utm_source'];

  window.collectEvent('beconEvent', 'page_loading', reportData);
};
